import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment-timezone'
import { useHistory, useParams } from 'react-router-dom'
import { uniqBy as _uniqBy } from 'lodash'
import axios from 'axios'
import { ArrowLeft } from 'lucide-react'
import mixpanel from '../../utilities/mixpanel'
import toast from '../../utilities/toast'

import { Button } from '../ui/button'
import CoachHeader from './CoachHeader/CoachHeader'
import StudentList from './StudentList/StudentList'
import { studentDisplayName } from '../../utilities/studentNames'
import SkillSelector from '../leevo_ui/SkillSelector/SkillSelector'
import LoadingSpinner from '../leevo_ui/LoadingSpinner/LoadingSpinner'
import Recommendations from './Recommendations/Recommendations'
import SkillDrawer from './SkillDrawer/SkillDrawer'
import ApplicationLayout from '../leevo_ui/ApplicationLayout/ApplicationLayout'
import SkillLevelList from '../leevo_ui/SkillLevelList/SkillLevelList'
import { requestCreator } from '../../utilities/requests'
import useFacility from '../../utilities/hooks/useFacility'

function useAttendanceApi(onUpdatedAttendance) {
  const [klassDaysStudents, setKlassDaysStudents] = useState([])

  function toggleAttendance(klassDaysStudentId) {
    const updatedStudent = klassDaysStudents.find(({ id }) => id === klassDaysStudentId)

    if (!updatedStudent) {
      const errorMsg = `Klasses student ${klassDaysStudentId} cannot be found`
      return Promise.reject(new Error(errorMsg))
    }
    const newStatus = !updatedStudent.attended
    updatedStudent.attended = newStatus
    setKlassDaysStudents(klassDaysStudents)

    return axios
      .patch(`/api/klass_days_students/${klassDaysStudentId}`, {
        attended: newStatus
      })
      .then(() => {
        onUpdatedAttendance()
        mixpanel.track('Marked Attendance')
      })
      .catch((error) => {
        updatedStudent.attended = !newStatus
        setKlassDaysStudents(klassDaysStudents)
        throw error
      })
  }

  return [setKlassDaysStudents, toggleAttendance]
}

const { get: getRecommendations, cancel: cancelRecommendationsRequest } = requestCreator()
const { get: getKlassDay, cancel: cancelKlassDaysRequest } = requestCreator()

function normalizeKlassDayPayload(klassDay) {
  if (!klassDay) return null
  return {
    ...klassDay,
    skillCategories: klassDay.skillCategories ?? []
  }
}

function buildFallbackKlassDayPayload({ klassDayDate, klassDayTime, timeZone }) {
  const fallbackDatetime = moment.tz(`${klassDayDate} ${klassDayTime}`, 'YYYY-MM-DD HH:mm', timeZone)
  return {
    id: null,
    coaches: [],
    students: [],
    klassDaysStudents: [],
    skillLevels: [],
    skillCategories: [],
    startDatetime: fallbackDatetime.isValid() ? fallbackDatetime.toISOString() : null
  }
}

// FIXME: This should be renamed to KlassDayShowFeature since this is how a class
// is shown and will also make it clear that the ID is a klass_day ID
// https://github.com/csalvato/leevo/pull/331#discussion_r393787492
function CoachFeature() {
  const history = useHistory()
  const {
    facility_id: facilityId,
    klass_id: klassId,
    klass_day_date: klassDayDate,
    klass_day_time: klassDayTime
  } = useParams()
  const { facility, isLoading: isFacilityLoading } = useFacility()
  const [setKlassDaysStudents, toggleAttendance] = useAttendanceApi(updateRecommendations)
  const [recommendations, setRecommendations] = useState([])
  const [isRecommendationsLoading, setIsRecommendationsLoading] = useState(true)
  const [
    { id: klassDayId, coaches, startDatetime, students, klassDaysStudents, skillLevels, skillCategories },
    setKlassDay
  ] = useState({})
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const autoAttendanceKlassDayIdRef = useRef(null)

  function updateRecommendations() {
    if (!klassDayId) {
      setRecommendations([])
      setIsRecommendationsLoading(false)
      return
    }

    setIsRecommendationsLoading(true)
    getRecommendations(`/api/recommendations/${klassDayId}`)
      .then((recs) => {
        setRecommendations(recs)
        setIsRecommendationsLoading(false)
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          setRecommendations([])
          setIsRecommendationsLoading(false)
        }
      })
  }

  useEffect(() => {
    if (!students) {
      setIsLoading(true)
      getKlassDay(`/api/classes/${klassId}/class_days/${klassDayDate}/${klassDayTime}`)
        .then((klassDay) => {
          const normalized = normalizeKlassDayPayload(klassDay)
          if (!normalized) {
            setKlassDay(
              buildFallbackKlassDayPayload({ klassDayDate, klassDayTime, timeZone: facility.timeZone || 'UTC' })
            )
            setSelectedCategory(null)
            setIsLoading(false)
            return
          }
          setKlassDay(normalized)
          setSelectedCategory(normalized.skillCategories?.[0] ?? null)
          if (klassDay.klassDaysStudents) {
            setKlassDaysStudents(klassDay.klassDaysStudents)
          }
          setIsLoading(false)
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            setKlassDay(
              buildFallbackKlassDayPayload({ klassDayDate, klassDayTime, timeZone: facility.timeZone || 'UTC' })
            )
            setSelectedCategory(null)
            setIsLoading(false)
          }
        })
    }

    return cancelKlassDaysRequest
  }, [klassDaysStudents, setKlassDaysStudents, students, facility, klassId, klassDayDate, klassDayTime])

  useEffect(() => {
    updateRecommendations()
    return cancelRecommendationsRequest
  }, [klassDayId]) /* eslint-disable-line */ // FIXME

  useEffect(() => {
    if (!klassDayId || !students || students.length !== 1) return
    if (!klassDaysStudents || klassDaysStudents.length !== 1) return
    if (klassDaysStudents[0].attended) return
    if (autoAttendanceKlassDayIdRef.current === klassDayId) return

    autoAttendanceKlassDayIdRef.current = klassDayId
    toggleAttendance(klassDaysStudents[0].id).catch(() => {
      autoAttendanceKlassDayIdRef.current = null
    })
  }, [klassDayId, students, klassDaysStudents, toggleAttendance])

  function extractLevelsFromStudents() {
    if (!students) return students
    return students
      .filter((student) => student.currentSkillLevel)
      .map((student) => ({
        id: student.currentSkillLevel.id,
        name: student.currentSkillLevel.name,
        skills: student.currentSkillLevel.skills
      }))
  }

  function handlePromotion({ id, currentSkillLevel }) {
    const promoted = students?.find((s) => s.id === id)
    const studentName = studentDisplayName(promoted) || 'This student'

    return axios
      .put(`/api/facilities/${facilityId}/students/${id}`, {
        student: {
          currentSkillLevelId: currentSkillLevel.nextSkillLevel.id
        }
      })
      .then(() => {
        mixpanel.track('Promoted Student', { studentId: id })
        return getKlassDay(`/api/classes/${klassId}/class_days/${klassDayDate}/${klassDayTime}`)
      })
      .then((klassDay) => {
        const normalized = normalizeKlassDayPayload(klassDay)
        if (normalized) {
          setKlassDay(normalized)
        }
        if (klassDay && !klassDay.students?.some((s) => s.id === id)) {
          toast.warning(`${studentName} was promoted and moved to a different class.`)
        }
      })
      .catch((error) => {
        if (!axios.isCancel(error)) throw error
      })
  }

  function studentLists(students, classLevels, klassDayId) {
    if (!klassDayId) return null

    return classLevels.map((level) => (
      <StudentList
        key={level.id}
        className="last:mb-24"
        level={level}
        klassDayId={klassDayId}
        onPromotion={handlePromotion}
        students={students.filter(
          (student) => student.currentSkillLevel && student.currentSkillLevel.name === level.name
        )}
        selectedCategory={selectedCategory}
        toggleAttendance={toggleAttendance}
        withSkillSelector={showSkillSelector}
      />
    ))
  }

  const classLevels = _uniqBy(extractLevelsFromStudents(), (e) => e.id).sort((a, b) => a.id - b.id)

  const showSkillSelector = skillCategories && skillCategories.length > 1

  function handleBackToDayClasses() {
    if (!startDatetime || !facility?.timeZone) return
    const m = moment(startDatetime).tz(facility.timeZone)
    const dayKey = m.format('YYYY-MM-DD')
    const monthKey = m.format('YYYY-MM')
    history.push(`/facilities/${facilityId}/calendar?day=${dayKey}&month=${monthKey}`)
  }

  const coachStartDateTime = startDatetime
    ? moment(startDatetime).tz(facility.timeZone)
    : moment.tz(`${klassDayDate} ${klassDayTime}`, 'YYYY-MM-DD HH:mm', facility.timeZone)

  return (
    <ApplicationLayout>
      {isFacilityLoading ? (
        <LoadingSpinner size="1/5" className="flex items-center justify-center m-10" />
      ) : (
        <>
          {students && (
            <SkillDrawer
              className="block md:hidden print:hidden"
              selectedCategory={selectedCategory}
              skillCategories={skillCategories ?? []}
              skillLevels={classLevels}
              onCategorySelected={setSelectedCategory}
            />
          )}
          {isLoading ? (
            <LoadingSpinner size="1/5" className="flex items-center justify-center m-10" />
          ) : (
            <div className="flex justify-center px-4 py-6 lg:px-8">
              <div className="w-full max-w-7xl">
                {startDatetime && facility?.timeZone && (
                  <div className="mb-4">
                    <Button variant="outline" type="button" onClick={handleBackToDayClasses} className="gap-2">
                      <ArrowLeft className="size-4" aria-hidden />
                      Back to day&apos;s classes
                    </Button>
                  </div>
                )}
                <CoachHeader
                  coaches={coaches || []}
                  classStartDateTime={coachStartDateTime}
                  skillLevels={skillLevels || []}
                  numStudents={students ? students.length : 0}
                />
                <Recommendations recommendations={recommendations} isLoading={isRecommendationsLoading} />
                <div className="mt-6 md:grid md:grid-cols-5 md:gap-6">
                  <div className="relative col-span-3">
                    {showSkillSelector && (
                      <div className="sticky top-0 z-20 mb-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                        <SkillSelector
                          skillCategories={skillCategories ?? []}
                          selectedCategory={selectedCategory}
                          onCategorySelected={setSelectedCategory}
                        />
                      </div>
                    )}

                    {students && studentLists(students, classLevels, klassDayId)}
                  </div>

                  <div className="hidden md:block md:col-span-2 print:block">
                    <div className="sticky top-6 h-[calc(100vh - 120px)] overflow-auto print:relative print:h-auto print:top-auto print:overflow-visible">
                      <SkillLevelList selectedCategory={selectedCategory} skillLevels={classLevels} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </ApplicationLayout>
  )
}

export default CoachFeature
